<script lang="ts">
  import { Loader } from '@googlemaps/js-api-loader'
  import {
    PUBLIC_GOOGLE_MAPS_API_KEY,
    PUBLIC_GOOGLE_MAPS_MAP_ID_DARK,
    PUBLIC_GOOGLE_MAPS_MAP_ID_LIGHT,
  } from '$env/static/public'
  import { browser } from '$app/environment'
  import { utils } from '$lib/utils'
  import StudioMarker from './StudioMarker.svelte'
  import type { Theme } from '@bruce/components'
  import { mount } from 'svelte'

  type MapOptions = Partial<
    NonNullable<ConstructorParameters<typeof google.maps.Map>[1]>
  >

  interface $$Props extends MapOptions {
    center: { lat: number; lng: number }
    zoom?: number
    markers?: { lat: number; lng: number }[]
    imageUrl?: string
    studios?: {
      latitude: number
      longitude: number
      studio_id: number
      name: string
      photo: string | null
    }[]
  }

  export let center: { lat: number; lng: number }
  export let zoom = 8
  export let markers: { lat: number; lng: number }[] | undefined = undefined
  export let imageUrl: string | undefined = undefined
  export let studios:
    | {
        latitude: number
        longitude: number
        studio_id: number
        name: string
        photo: string | null
      }[]
    | undefined = undefined

  let mapId = PUBLIC_GOOGLE_MAPS_MAP_ID_LIGHT
  let prefersDark = false
  let storedTheme: Theme | undefined = undefined

  if (browser) {
    prefersDark =
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches

    storedTheme = utils.theme.get()

    if (storedTheme === 'dark' || (!storedTheme && prefersDark)) {
      mapId = PUBLIC_GOOGLE_MAPS_MAP_ID_DARK
    }
  }

  let mapLight: HTMLDivElement | undefined
  let mapDark: HTMLDivElement | undefined
  let map: google.maps.Map | undefined
  let zIndex = 1

  async function loadGoogleMaps(
    mapElement: HTMLDivElement,
    mapOptions: MapOptions,
  ) {
    const loader = new Loader({
      apiKey: PUBLIC_GOOGLE_MAPS_API_KEY,
      version: 'weekly',
    })

    const { AdvancedMarkerElement, PinElement } =
      await loader.importLibrary('marker')
    const { Map } = await loader.importLibrary('maps')

    map = new Map(mapElement, {
      ...mapOptions,
      mapId,
    })

    if (studios) {
      studios.forEach((studio) => {
        const div = document.createElement('div')

        if (imageUrl) {
          mount(StudioMarker, {
            props: {
              imageUrl,
              studio: {
                id: studio.studio_id,
                name: studio.name,
                photo: studio.photo,
              },
            },
            target: div,
          })
        }

        const advancedMarker = new AdvancedMarkerElement({
          position: { lat: studio.latitude, lng: studio.longitude },
          map,
          content: div,
        })

        advancedMarker.addListener('click', () => {
          advancedMarker.zIndex = zIndex
          zIndex = zIndex + 1
        })
      })
    } else if (markers) {
      markers.forEach((marker) => {
        const pin = new PinElement({
          background: mapLight ? '#000' : '#d8fd53',
          borderColor: mapLight ? '#000' : '#d8fd53',
          glyphColor: mapLight ? '#fff' : '#000',
        })

        new AdvancedMarkerElement({
          position: marker,
          map,
          content: pin.element,
        })
      })
    }
  }

  function checkTheme(_: Element) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && map) {
          zoom = map.getZoom() || zoom
          center = map.getCenter()?.toJSON() || center

          if (mutation.oldValue === '') {
            mapId = PUBLIC_GOOGLE_MAPS_MAP_ID_LIGHT
          } else {
            mapId = PUBLIC_GOOGLE_MAPS_MAP_ID_DARK
          }
        }
      })
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['dark-theme'],
    })

    return {
      destroy() {
        observer.disconnect()
      },
    }
  }

  $: if (browser) {
    if (mapLight) {
      loadGoogleMaps(mapLight, {
        center,
        zoom,
        ...$$restProps,
      })
    } else if (mapDark) {
      loadGoogleMaps(mapDark, {
        center,
        zoom,
        ...$$restProps,
      })
    }
  }
</script>

<div use:checkTheme>
  {#if mapId === PUBLIC_GOOGLE_MAPS_MAP_ID_LIGHT}
    <div bind:this={mapLight}></div>
  {:else}
    <div bind:this={mapDark}></div>
  {/if}
</div>

<style lang="scss">
  div {
    height: 100%;
  }
</style>
